import * as Toast from '@radix-ui/react-toast'
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { useT } from '../i18n'

/**
 * Kurze Rückmeldungen — „kopiert", „in den Warenkorb gelegt", „3 von 5 Artikeln
 * übernommen".
 *
 * # Warum es das braucht
 *
 * Bisher meldete der Laden solche Dinge entweder gar nicht oder nur an eine
 * Sprachausgabe (`nur-fuer-screenreader` mit role="status"). Das ist genau
 * verkehrt herum gedacht: Wer sieht, bekam nichts; wer nicht sieht, bekam alles.
 *
 * Der Kopierknopf an der Bestellnummer ist das deutlichste Beispiel. Er
 * wechselte sein Symbol für zwei Sekunden — an einer Stelle, auf die gerade
 * niemand schaut, weil man nach dem Klicken zum Einfügen woanders hin geht.
 *
 * # Warum Radix
 *
 * Eine Meldung, die von selbst verschwindet, ist schwerer als sie aussieht:
 * Sie muss angesagt werden, ohne den Fokus zu stehlen; sie darf nicht
 * verschwinden, während jemand mit der Maus darauf steht oder mit der Tastatur
 * darin ist; mehrere gleichzeitig müssen sich stapeln statt überdecken; und auf
 * dem Telefon muss sie sich wegwischen lassen. Radix Toast bringt das mit —
 * samt der Feinheit, dass ein Fenster im Hintergrund die Zeit anhält.
 */

type Melden = (text: string) => void

const Zusammenhang = createContext<Melden>(() => {})

/** Eine Meldung absetzen. Tut nichts, wenn der Laden noch nicht steht. */
export function useMeldung(): Melden {
  return useContext(Zusammenhang)
}

interface Eintrag {
  id: number
  text: string
}

export function Meldungen({ children }: { children: ReactNode }) {
  const t = useT()
  const [eintraege, setEintraege] = useState<Eintrag[]>([])

  const melden = useCallback((text: string) => {
    // Eine fortlaufende Zahl als Schlüssel und nicht der Text: Zweimal
    // dasselbe kopiert ergäbe sonst zwei Meldungen mit demselben Schlüssel,
    // und React zeigte die zweite nicht an.
    setEintraege((vorher) => [...vorher, { id: Date.now() + Math.random(), text }])
  }, [])

  return (
    <Zusammenhang.Provider value={melden}>
      <Toast.Provider swipeDirection="right" duration={4000} label={t('meldung.aria')}>
        {children}

        {eintraege.map((eintrag) => (
          <Toast.Root
            key={eintrag.id}
            className="meldung"
            onOpenChange={(offen) => {
              if (!offen) {
                setEintraege((vorher) => vorher.filter((e) => e.id !== eintrag.id))
              }
            }}
          >
            <Toast.Description className="meldung-text">{eintrag.text}</Toast.Description>
            <Toast.Close className="meldung-zu" aria-label={t('meldung.schliessen')}>
              ×
            </Toast.Close>
          </Toast.Root>
        ))}

        <Toast.Viewport className="meldungen" />
      </Toast.Provider>
    </Zusammenhang.Provider>
  )
}
